import { Button, Typography } from 'antd';
import type { ArgsProps } from 'antd/es/notification/interface';
import { notification } from 'packages/lib/common-ui/notification/notify';

type Config = ArgsProps & {
  message: string;
  description?: string;
  key?: string;
};

type ActiveNotification = {
  count: number;
  message: string;
  descriptions: string[];
};

type NotifyMethod = 'success' | 'error' | 'info' | 'warning' | 'open';

/**
 * This class is used to solve a problem regarding legacy toast/notification spam. This codebase
 * has many instances of notifications being dispatched many levels away from their local call site,
 * such as within fetch/api helpers. This resulted in 10-100s of individually dismissable notifications (annoying...)
 *
 * This class mitigates these problems by
 * - Deduping notifications that have the same message
 * - Aggregating deduped notifications via `description` to enable deduping of notifications with UUIDs
 * - Adding a "Dismiss All" button to notifications when activeNotifications.size > 1
 *
 * Prefer to use `notification.useNotification` as deduping should not be necessary.
 *
 * Examples:
 *  - Two calls of dNotify.error({message: 'Uh oh'})
 *    -------------------
 *    | (2) Uh oh       |
 *    ___________________
 *
 * - Two calls of dNotify.error({message: 'Uh oh', description: Math.random()})
 *   -------------------
 *   | (2) Uh oh       |
 *   |   - 0.145       | // Scrollable list
 *   |   - 0.984       |
 *   -------------------
 */
export class DedupedNotify {
  activeNotifications: Map<string, ActiveNotification>;

  constructor() {
    this.activeNotifications = new Map();
  }

  success(config: Config): void {
    this.handle('success', config);
  }

  error(config: Config): void {
    this.handle('error', { duration: 0, ...config });
  }

  info(config: Config): void {
    this.handle('info', config);
  }

  warning(config: Config): void {
    this.handle('warning', config);
  }

  open(config: Config): void {
    this.handle('open', { duration: 0, ...config });
  }

  dismissAll(): void {
    notification.destroy();
    this.activeNotifications.clear();
  }

  private handle(method: NotifyMethod, config: Config): void {
    const { key, message, description, onClose, props, ...rest } = config;

    const dKey = key || generateKey(method, message);

    let current = this.activeNotifications.get(dKey);
    if (!current || current.message !== message) {
      current = { count: 0, descriptions: [], message: message };
      this.activeNotifications.set(dKey, current);
    }

    current.count += 1;
    if (description) {
      current.descriptions.push(description);
    }

    let dMessage = message;
    if (current.count > 1) {
      dMessage = `(${current.count}) ${dMessage}`;
    }

    const payload: ArgsProps = {
      key: dKey,
      message: dMessage,
      description:
        current.descriptions.length > 0 ? (
          <NotificationDescriptions descriptions={current.descriptions} />
        ) : null,
      actions:
        this.activeNotifications.size > 1 ? (
          <DismissAllButton onDismissAll={() => this.dismissAll()} />
        ) : null,
      onClose: () => {
        this.activeNotifications.delete(dKey);
        onClose?.();
      },
      props: { ...props, 'data-testid': `notification-${method}` },
      ...rest,
    };

    if (method === 'open') {
      notification.open(payload);
    } else {
      notification[method](payload);
    }
  }
}

export const dNotify = new DedupedNotify();

// Internal ------------------------------------
export function generateKey(method: NotifyMethod, message: string) {
  return `${method}:${message}`;
}

function DismissAllButton({ onDismissAll }: { onDismissAll: () => void }) {
  return <Button onClick={onDismissAll}>Dismiss All</Button>;
}

function NotificationDescriptions({
  descriptions,
}: {
  descriptions: string[];
}) {
  return (
    <div
      style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginTop: '8px',
      }}
    >
      <ul
        style={{
          margin: 0,
          paddingLeft: '16px',
          listStyle: 'disc',
        }}
      >
        {descriptions.map((item, index) => (
          <li key={index} style={{ padding: '2px 0' }}>
            <Typography.Text
              type="secondary"
              style={{
                fontSize: '12px',
                wordBreak: 'break-all',
                display: 'block',
              }}
            >
              - {item}
            </Typography.Text>
          </li>
        ))}
      </ul>
    </div>
  );
}
